import * as ko from "knockout";
import {global, config, lm_} from "../appContext";
import {IResponse} from "../index";
import {KOSubscriptionTracker} from "../util/KOSubscriptionTracker";
import {_d, getEadmRole} from "../util/misc";
import {Permission} from "../constants/Permission";

interface IHubSpotVisitorTokenResult {
    token: string;
    email: string;
}

interface IInlineChatCallbacks {
    onLoaded?: () => void;
}

class HubSpotSupport {

    private static readonly COPILOT_EMBED_SELECTOR = "#hubspot-copilot-embed";

    private readonly kosub = new KOSubscriptionTracker();
    private subscriptionsBound = false;
    private trackerStarted = false;
    private tokenRefreshTimer: number | null = null;
    private identifyTimeoutTimer: number | null = null;
    private visitorIdentity = ko.observable<IHubSpotVisitorTokenResult | null>(null);
    private identifyFinished = ko.observable(false);
    private identifyTimedOut = false;
    private inlineChatWaitSubscription: KnockoutSubscription | null = null;
    private inlineChatGeneration = 0;
    private widgetLoadedHandler: ((payload?: unknown) => void) | null = null;

    init(): void {
        this.visitorIdentity(null);
        this.identifyFinished(false);
        this.identifyTimedOut = false;
        this.syncIdentitySettings();
        this.tokenRefreshTimer = this.clearTimer(this.tokenRefreshTimer);
        this.identifyTimeoutTimer = this.clearTimer(this.identifyTimeoutTimer);

        if (!this.canTrack()) {
            this.removeWidget();
            return;
        }

        window.hsConversationsSettings = {
            ...window.hsConversationsSettings,
            loadImmediately: false,
            inlineEmbedSelector: HubSpotSupport.COPILOT_EMBED_SELECTOR
        };

        if (!this.subscriptionsBound) {
            this.subscriptionsBound = true;

            this.kosub.subscribe(global.router.currentRoute, () => {
                this.trackPageView();
            });
        }

        if (!this.isChatAvailable()) {
            this.removeWidget();
            this.startTracking();
            return;
        }

        this.identifyTimeoutTimer = window.setTimeout(() => {
            this.identifyTimeoutTimer = null;
            if (this.identifyFinished()) return;
            _d.warn("HubSpot identification timed out; chat continues anonymously");
            this.identifyTimedOut = true;
            this.identifyFinished(true);
            this.startTracking();
        }, 5000);

        void this.identifyVisitor().then(() => this.startTracking());
    }

    isChatAvailable(): boolean {
        return this.canTrack()
            && getEadmRole(global.login.getHomeOrg()) >= Permission.SuperManager;
    }

    loadInlineChat(callbacks?: IInlineChatCallbacks): void {
        if (!this.isChatAvailable()) {
            callbacks?.onLoaded?.();
            return;
        }

        this.clearInlineChatListeners();
        const generation = ++this.inlineChatGeneration;
        const isCurrent = () => generation === this.inlineChatGeneration;

        const finish = () => {
            if (!isCurrent()) return;
            this.clearInlineChatListeners();
            callbacks?.onLoaded?.();
        };

        const run = async () => {
            if (!isCurrent()) return;

            if (!this.identifyFinished()) {
                await new Promise<void>((resolve) => {
                    this.inlineChatWaitSubscription?.dispose();
                    this.inlineChatWaitSubscription = this.identifyFinished.subscribe((finished) => {
                        if (!finished) {
                            return;
                        }
                        this.inlineChatWaitSubscription?.dispose();
                        this.inlineChatWaitSubscription = null;
                        resolve();
                    });
                });
            }
            if (!isCurrent()) return;

            if (!document.querySelector(HubSpotSupport.COPILOT_EMBED_SELECTOR)) {
                finish();
                return;
            }

            const conversations = window.HubSpotConversations;
            const widget = conversations?.widget;
            if (!conversations || !widget) {
                finish();
                return;
            }

            this.syncIdentitySettings();

            try {
                if (widget.status().loaded) {
                    widget.refresh({ openToNewThread: false });
                    finish();
                    return;
                }

                this.widgetLoadedHandler = () => finish();
                conversations.on("widgetLoaded", this.widgetLoadedHandler);
                widget.load();
            } catch (err) {
                _d.warn("HubSpot inline chat could not be loaded", err);
                finish();
            }
        };

        if (window.HubSpotConversations?.widget) {
            void run();
        } else {
            window.hsConversationsOnReady = window.hsConversationsOnReady || [];
            window.hsConversationsOnReady.push(() => {
                if (!isCurrent()) return;
                void run();
            });
        }
    }

    unloadInlineChat(): void {
        this.inlineChatGeneration++;
        this.clearInlineChatListeners();
        this.removeWidget();
    }

    private clearInlineChatListeners(): void {
        this.inlineChatWaitSubscription?.dispose();
        this.inlineChatWaitSubscription = null;
        if (this.widgetLoadedHandler) {
            window.HubSpotConversations?.off("widgetLoaded", this.widgetLoadedHandler);
            this.widgetLoadedHandler = null;
        }
    }

    private startTracking(): void {
        if (!this.canTrack()) {
            return;
        }

        window._hsq = window._hsq || [];

        const identity = this.visitorIdentity();
        if (identity) {
            window._hsq.push(["identify", { email: identity.email }]);
        }

        window._hsq.push(["setPath", window.location.hash || "/"]);

        if (this.trackerStarted) {
            window._hsq.push(["trackPageView"]);
            return;
        }

        if (document.getElementById("hs-script-loader")) {
            return;
        }

        // HubSpot analytics may call Chrome's perf reporter without a typeof guard.
        if (typeof window.__chromium_devtools_metrics_reporter !== "function") {
            window.__chromium_devtools_metrics_reporter = () => {};
        }

        _d.log("HubSpot initialization start");
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.id = "hs-script-loader";
        script.async = true;
        script.defer = true;
        script.src = config.HUBSPOT_SCRIPT_URL;
        script.addEventListener("load", () => {
            _d.log("HubSpot initialized");
            this.trackerStarted = true;
        });
        document.body.append(script);
    }

    private trackPageView(): void {
        if (!this.canTrack()) {
            return;
        }

        window._hsq = window._hsq || [];
        window._hsq.push(["setPath", window.location.hash || "/"]);

        if (this.trackerStarted) {
            window._hsq.push(["trackPageView"]);
        }
    }

    private canTrack(): boolean {
        









        if (global.loginManager?.isEmulated()) return false;
        if (!global.login?.isLoggedIn?.()) return false;
        return true;
    }

    private clearTimer(timer: number | null): null {
        if (timer !== null) {
            window.clearTimeout(timer);
        }
        return null;
    }

    private removeWidget(): void {
        try {
            window.HubSpotConversations?.widget?.remove();
        } catch (err) {
            _d.warn("HubSpot widget could not be removed", err);
        }
    }

    private identifyVisitor(): Promise<boolean> {
        return global.getPromiseRequest("identifyhubspotcontact")
            .request({
                preferredlanguage: lm_.selectedLanguage()?.key ?? '',
            }, true, {}, { errorHandler: () => {} })
            .then((response: IResponse<IHubSpotVisitorTokenResult[]>) => {
                const result = response.data?.[0];
                if (typeof result?.token === "string" && result.token.trim().length > 0
                    && typeof result.email === "string" && result.email.trim().length > 0) {
                    this.visitorIdentity(result);
                    this.syncIdentitySettings();

                    // HubSpot visitor tokens are valid for 12 hours; refresh at 11h to stay ahead of expiry
                    this.tokenRefreshTimer = this.clearTimer(this.tokenRefreshTimer);
                    this.tokenRefreshTimer = window.setTimeout(() => {
                        if (!this.canTrack()) return;
                        this.identifyVisitor().then((identified) => {
                            if (identified) {
                                window.HubSpotConversations?.widget?.refresh({ openToNewThread: false });
                            }
                        });
                    }, 11 * 60 * 60 * 1000);

                    if (this.identifyTimedOut) {
                        this.identifyTimedOut = false;
                        window.HubSpotConversations?.widget?.refresh({ openToNewThread: false });
                    }
                    return true;
                }
                _d.warn("HubSpot visitor token response was invalid", response);
                return false;
            })
            .catch((err: unknown) => {
                const response = err as IResponse | null;
                if (response?.returncode === "2") {
                    _d.log("HubSpot identification declined:", response.lasterror);
                } else {
                    _d.warn("HubSpot identification failed", err);
                }
                return false;
            })
            .finally(() => {
                this.identifyTimeoutTimer = this.clearTimer(this.identifyTimeoutTimer);
                this.identifyFinished(true);
            });
    }

    private syncIdentitySettings(): void {
        const identity = this.visitorIdentity();
        if (!identity) {
            if (window.hsConversationsSettings) {
                delete window.hsConversationsSettings.identificationEmail;
                delete window.hsConversationsSettings.identificationToken;
            }
            return;
        }

        window.hsConversationsSettings = {
            ...window.hsConversationsSettings,
            identificationEmail: identity.email,
            identificationToken: identity.token,
        };
    }

}

export const hubSpotSupport = new HubSpotSupport();
